import React from "react";
import { Link } from "react-router-dom";
import { CommonBanner, Footer, FreeAssessment, Header } from "../../components";
import { C, FONT, sectionTitle, sectionLead } from "../../constants/mq";

// About Us.
//
// The old page opened with "Welcome to CS Online School" on a CS Education
// menu item on connectedschool.com.au — three different names for the same
// visitor. This page is CS Education throughout, and says plainly what we do
// rather than describing ourselves as well-established.

const whatWeDo = [
  {
    title: "We write our own material",
    body: "Every course book, worksheet and assessment is written in-house. When a cohort struggles with a topic, we change the material rather than working around it.",
  },
  {
    title: "We teach in small classes",
    body: "Classes are kept small enough that a teacher can tell which students have understood something and which have not, and act on it during the lesson.",
  },
  {
    title: "We follow the whole term",
    body: "Lectures, self-directed study, homework, tutoring and a summative assessment run across each term, so progress is something you can see rather than guess at.",
  },
  {
    title: "We teach on campus and online",
    body: "Attend at a campus or join online. The material, the teachers and the assessments are the same either way, and students can move between the two.",
  },
];

const programs = [
  {
    name: "Primary — Year 1 to Year 6",
    body: "WRMT (Writing, Reading skills, Mathematical reasoning skills and Thinking skills), plus preparation for the OC and Selective School tests and the move into high school.",
  },
  {
    name: "High School — Year 7 to Year 10",
    body: "English and Writing, Mathematics and Science, each written for the specific year level rather than a general ability band.",
  },
  {
    name: "Senior — Year 11 and Year 12",
    body: "HSC English, Mathematics from Advanced through to Extension 2, and Physics, Chemistry and Biology, with a free weekly tutorial alongside the classes.",
  },
  {
    name: "Holiday and trial test courses",
    body: "Shorter, focused courses between terms, and full trial tests under exam conditions for students sitting OC or Selective.",
  },
];

// White and sand sections only; text sits in the banner's 1072 column at a
// readable measure, and "How we work" is a centred intro over white cards.
const s = {
  section: { background: "#ffffff", padding: "64px 0" },
  sectionAlt: { background: C.sand, padding: "72px 0 80px" },
  container: { maxWidth: 1072, margin: "0 auto", padding: "0 24px" },
  narrow: { maxWidth: 780 },
  lead: {
    fontSize: 19,
    lineHeight: 1.7,
    color: C.body,
    fontWeight: 500,
    margin: "0 0 20px",
  },
  para: { fontSize: 15.5, lineHeight: 1.8, color: C.body, margin: "0 0 16px" },
  introTitle: { ...sectionTitle },
  introSub: { ...sectionLead, margin: "0 auto 36px" },
  h2: {
    fontSize: 26,
    fontWeight: 700,
    color: C.ink,
    margin: "0 0 8px",
    letterSpacing: "-0.3px",
  },
  h2sub: { fontSize: 15, color: C.muted, margin: "0 0 32px", lineHeight: 1.6 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 20,
  },
  card: { padding: "24px 22px 26px" },
  cardTitle: { fontSize: 17, fontWeight: 700, color: C.ink, margin: "0 0 8px" },
  cardBody: { fontSize: 14.5, lineHeight: 1.7, color: C.body, margin: 0 },
  progRow: {
    borderTop: `1px solid ${C.line}`,
    padding: "22px 0",
  },
  progName: { fontSize: 16, fontWeight: 700, color: C.ink, margin: "0 0 6px" },
  progBody: { fontSize: 14.5, lineHeight: 1.7, color: C.body, margin: 0 },
  ctaRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "16px 32px", marginTop: 36 },
};

const banner = {
  bannerHeading: "About CS Education",
  subheading:
    "Structured programs from Kindergarten to Year 12, written and taught by us, on campus and online.",
  path: [{ link: "/about-us", name: "About Us" }],
};

function AboutUs() {
  return (
    <div style={{ background: "#ffffff", fontFamily: FONT }}>
      <Header />
      <CommonBanner {...banner} />

      <main>
        <section style={s.section}>
          <div style={s.container}>
            <div style={s.narrow}>
              <p style={s.lead}>
                We teach school students. That has been the whole of our work
                since 2002, when we opened a single classroom in Castle Hill.
              </p>
              <p style={s.para}>
                What began as preparation for the OC and Selective School exams
                now runs from Kindergarten through to the HSC, at campuses across
                Sydney and online. Along the way we built the parts we could not
                buy: our own course books, our own marking, and the learning
                platform students and parents use between lessons.
              </p>
              <p style={s.para}>
                That matters for a practical reason. Because we write the
                material, teach the classes and mark the assessments ourselves,
                we can see where a year group is losing ground and change what we
                teach next term. Nothing has to wait for someone else&rsquo;s
                revision cycle.
              </p>
            </div>
          </div>
        </section>

        <section style={s.sectionAlt}>
          <div style={s.container}>
            <h2 style={s.introTitle}>How we work</h2>
            <p style={s.introSub}>
              Four things shape every course we run.
            </p>
            <div style={s.grid}>
              {whatWeDo.map((c) => (
                <div key={c.title} className="mq-card mq-card--hover" style={s.card}>
                  <h3 style={s.cardTitle}>{c.title}</h3>
                  <p style={s.cardBody}>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={s.section}>
          <div style={s.container}>
            <div style={s.narrow}>
              <h2 style={s.h2}>What we teach</h2>
              <p style={s.h2sub}>
                Each program is written for a specific year level.
              </p>
              {programs.map((p) => (
                <div key={p.name} style={s.progRow}>
                  <h3 style={s.progName}>{p.name}</h3>
                  <p style={s.progBody}>{p.body}</p>
                </div>
              ))}

              <div style={s.ctaRow}>
                <Link className="mq-btn" to="/our-courses">
                  Browse courses
                </Link>
                <Link className="mq-link" to="/our-history">
                  Our history
                </Link>
                <Link className="mq-link" to="/contact-us">
                  Talk to us
                </Link>
              </div>
            </div>
          </div>
        </section>

        <FreeAssessment />
      </main>

      <Footer />
    </div>
  );
}

export default AboutUs;
